<script lang="ts">
  // One card: real art from the CDN, with a styled text-frame fallback for
  // passcodes the CDN lacks. Catalog tiles grey + lock when unowned; deck-zone
  // tiles (`removable`) show a remove cue on hover.
  import type { CardInfo } from "@ygoevo/yo2-data";
  import { cardImage } from "../../data.ts";
  import { frameColor } from "../cards.ts";
  import { foilClass, foilGradeClass, foilColor } from "../rarity.ts";

  let {
    card,
    owned = 0,
    used = 0,
    foil = 0,
    dim = true,
    removable = false,
    lifting = false,
    selecting = false,
    selected = false,
    onclick,
    oncontextmenu,
    onactivate,
    onhover,
    onpointerdown,
  }: {
    card: CardInfo;
    owned?: number;
    /** copies already placed in the deck (for the count badge) */
    used?: number;
    /** best foil grade owned (1..3); 0 = only plain. Drives the holographic finish. */
    foil?: number;
    /** grey + lock when unowned (catalog); off for deck-zone tiles */
    dim?: boolean;
    /** deck-zone tile, show a remove cue on hover */
    removable?: boolean;
    /** this tile is the one currently being dragged, render it as a hollow gap */
    lifting?: boolean;
    /** bulk-selection mode: click toggles this card instead of starting a drag */
    selecting?: boolean;
    /** this card/finish is part of the current bulk selection */
    selected?: boolean;
    onclick?: () => void;
    /** right-click, remove a copy (pool) / remove this copy (deck) */
    oncontextmenu?: (e: MouseEvent) => void;
    /** keyboard activation (Enter/Space), drag uses pointerdown instead of click */
    onactivate?: () => void;
    onhover?: () => void;
    onpointerdown?: (e: PointerEvent) => void;
  } = $props();

  let broken = $state(false);
  const isOwned = $derived(owned > 0);
  const fmt = (v: number | null) => (v === null ? "" : v === -2 ? "?" : String(v));

  function onkeydown(e: KeyboardEvent) {
    if (!onactivate) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onactivate();
    }
  }
</script>

<button
  type="button"
  {onclick}
  {oncontextmenu}
  {onpointerdown}
  disabled={selecting && !isOwned}
  aria-pressed={selecting ? selected : undefined}
  onkeydown={onactivate ? onkeydown : undefined}
  onfocus={onhover}
  title={selecting ? `${card.name} — ${selected ? "selected for dismantling" : isOwned ? "select for dismantling" : "no copies in this finish"}` : removable ? `${card.name} — double-click to remove` : card.name}
  class="group relative block aspect-[59/86] w-full overflow-hidden chamfer-btn transition-[transform,opacity] duration-150 hover:z-10 hover:-translate-y-0.5 {foil ? '' : foilClass(card.rarity)} {lifting
    ? 'scale-95 opacity-25 saturate-50'
    : ''} {selected ? 'z-10 ring-2 ring-cyan glow-cyan' : ''} {selecting
    ? isOwned
      ? 'cursor-pointer hover:glow-cyan'
      : 'cursor-not-allowed opacity-55'
    : !isOwned && dim
      ? 'cursor-default'
    : removable
      ? 'cursor-grab hover:glow-magenta active:cursor-grabbing'
      : 'cursor-grab hover:glow-cyan active:cursor-grabbing'}"
  style="--c:5px; touch-action:none; -webkit-user-select:none; user-select:none"
>
  <!-- art wrapper carries the foil finish, so its sheen sits under the badges below -->
  <div class="size-full {foilGradeClass(foil)}">
    {#if !broken}
      <img src={cardImage(card.id)} alt={card.name} onerror={() => (broken = true)} class="size-full object-cover" draggable="false" />
    {:else}
      <div class="flex size-full flex-col" style="background:{frameColor(card)}">
        <div class="truncate px-1 pt-1 text-center text-[0.5rem] font-bold leading-tight text-white/95" style="text-shadow:0 1px 1px rgba(0,0,0,.6)">{card.name}</div>
        <div class="m-1 flex-1 bg-black/25"></div>
        {#if card.category === "monster" || card.category === "monster-extra"}
          <div class="px-1 pb-1 text-right text-[0.5rem] font-bold tabular-nums text-white/95" style="text-shadow:0 1px 1px rgba(0,0,0,.6)">
            {fmt(card.atk)}{card.def !== null ? `/${fmt(card.def)}` : ""}
          </div>
        {:else}
          <div class="px-1 pb-1 text-center text-[0.5rem] uppercase tracking-wide text-white/90">{card.category}</div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Unowned veil + lock (catalog only). -->
  {#if dim && !isOwned}
    <div class="absolute inset-0 grid place-items-center bg-void/72">
      <span class="font-display text-sm font-bold text-ink-dim">{selecting ? "—" : "🔒"}</span>
    </div>
  {/if}

  <!-- Owned-count badge (catalog only). -->
  {#if dim && isOwned}
    <span class="absolute bottom-0.5 right-0.5 chamfer-btn bg-void/85 px-1 font-display text-[0.55rem] font-bold leading-tight text-cyan" style="--c:2px">{used}/{owned}</span>
  {/if}

  <!-- Foil marker, you own a foil of this card (tinted by grade). -->
  {#if foil > 0}
    <span
      class="pointer-events-none absolute left-0.5 top-0.5 text-[0.7rem] leading-none"
      style="color:{foilColor(foil)};text-shadow:0 0 5px {foilColor(foil)},0 0 2px rgba(0,0,0,.6)"
      title="You own a foil of this card"
    >✦</span>
  {/if}

  {#if selecting && selected}
    <span class="pointer-events-none absolute right-0.5 top-0.5 grid size-5 place-items-center chamfer-btn bg-cyan font-display text-xs font-black text-void shadow-[0_0_8px_rgba(25,224,255,.65)]" style="--c:3px">✓</span>
  {/if}

  <!-- Remove cue (deck-zone tiles). -->
  {#if removable}
    <div class="pointer-events-none absolute inset-0 grid place-items-center bg-magenta/35 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      <span class="grid size-5 place-items-center rounded-full bg-void/85 text-xs font-bold text-magenta">✕</span>
    </div>
  {/if}
</button>
